import React, { useMemo, useState } from 'react';
import Grid from '@mui/material/GridLegacy';
import { Box, Typography, Card, CardContent, List, ListItem, ListItemText, Button, Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, LinearProgress } from '@mui/material';
import { LocalOffer, AttachMoney } from '@mui/icons-material';

export default function AnalyticsTab({ analytics = {}, redemptions = [] }) {
  const redeemed = analytics.redeemedOffers || {};
  const [analyticsSortState, setAnalyticsSortState] = useState({
    financialPlace: { key: 'total', direction: 'desc' },
    financialOffer: { key: 'total', direction: 'desc' },
    financialAmbassador: { key: 'total', direction: 'desc' },
    place: { key: 'total', direction: 'desc' },
    category: { key: 'total', direction: 'desc' },
    offer: { key: 'total', direction: 'desc' },
    ambassador: { key: 'count', direction: 'desc' },
  });

  const handleAnalyticsSort = (section, key) => {
    setAnalyticsSortState((prev) => {
      const current = prev[section] || { key: 'total', direction: 'desc' };
      if (current.key === key) {
        return {
          ...prev,
          [section]: { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        };
      }
      return {
        ...prev,
        [section]: { key, direction: 'desc' }
      };
    });
  };

  const renderSortButton = (section, key, label) => {
    const sectionSort = analyticsSortState[section] || { key: 'total', direction: 'desc' };
    const active = sectionSort.key === key;
    return (
      <Button
        size="small"
        variant="text"
        onClick={() => handleAnalyticsSort(section, key)}
        aria-label={label}
        sx={{
          minWidth: 0,
          px: 0.5,
          py: 0.25,
          textTransform: 'none',
          fontWeight: 700,
          color: active ? 'primary.main' : 'text.primary',
        }}
      >
        {label}
        {active && <Box component="span" sx={{ ml: 0.5, fontSize: '0.8rem' }}>{sectionSort.direction === 'asc' ? '↑' : '↓'}</Box>}
      </Button>
    );
  };

  const redeemedCommissionSummary = useMemo(() => {
    const byPlace = new Map();
    const byOffer = new Map();
    const byCategory = new Map();
    const byAmbassador = new Map();

    (redemptions || []).forEach((r) => {
      const offerComm = Number(r.offerCommissionLe ?? 0);
      const ambassadorComm = Number(r.ambassadorCommissionLe ?? 0);
      const neozoolaComm = Number((offerComm - ambassadorComm).toFixed(2));
      const placeKey = String(r.placeId?._id || r.placeId || 'unknown-place');
      const placeName = r.placeId?.name || r.placeId || 'Unknown place';
      const offerKey = `${placeKey}:${r.offerId || 'unknown-offer'}`;
      const category = r.placeId?.category || 'Uncategorized';
      const ambassadorCode = r.ambassadorCode || 'No ambassador';

      if (!byPlace.has(placeKey)) {
        byPlace.set(placeKey, { placeId: placeKey, placeName, count: 0, total: 0, neozoola: 0, ambassador: 0 });
      }
      const placeStats = byPlace.get(placeKey);
      placeStats.count += 1;
      placeStats.total += offerComm;
      placeStats.neozoola += neozoolaComm;
      placeStats.ambassador += ambassadorComm;

      if (!byOffer.has(offerKey)) {
        byOffer.set(offerKey, {
          placeId: placeKey,
          placeName,
          offerId: r.offerId || 'unknown-offer',
          offerName: r.offerName || r.offerId || 'Unknown offer',
          count: 0,
          offerCommissionLe: 0,
          total: 0,
          neozoola: 0,
          ambassador: 0,
        });
      }
      const offerStats = byOffer.get(offerKey);
      offerStats.count += 1;
      offerStats.offerCommissionLe += offerComm;
      offerStats.total += offerComm;
      offerStats.neozoola += neozoolaComm;
      offerStats.ambassador += ambassadorComm;

      if (!byCategory.has(category)) {
        byCategory.set(category, { category, count: 0, total: 0, neozoola: 0, ambassador: 0 });
      }
      const categoryStats = byCategory.get(category);
      categoryStats.count += 1;
      categoryStats.total += offerComm;
      categoryStats.neozoola += neozoolaComm;
      categoryStats.ambassador += ambassadorComm;

      if (!byAmbassador.has(ambassadorCode)) {
        byAmbassador.set(ambassadorCode, { code: ambassadorCode, count: 0, total: 0, neozoola: 0, ambassador: 0 });
      }
      const ambassadorStats = byAmbassador.get(ambassadorCode);
      ambassadorStats.count += 1;
      ambassadorStats.total += offerComm;
      ambassadorStats.neozoola += neozoolaComm;
      ambassadorStats.ambassador += ambassadorComm;
    });

    return {
      byPlace: Array.from(byPlace.values()).sort((a, b) => b.total - a.total),
      byOffer: Array.from(byOffer.values()).sort((a, b) => b.total - a.total),
      byCategory: Array.from(byCategory.values()).sort((a, b) => b.total - a.total),
      byAmbassador: Array.from(byAmbassador.values()).sort((a, b) => b.total - a.total),
    };
  }, [redemptions]);

  // Calculate commission summary from all redemptions
  const commissionSummary = React.useMemo(() => {
    const summary = {
      totalOfferCommission: 0,
      totalNeozoolaCommission: 0,
      totalAmbassadorCommission: 0,
      byPlace: new Map(),
      byOffer: new Map(),
      byCategory: new Map(),
      byAmbassador: new Map(),
    };

    (redemptions || []).forEach((r) => {
      const offerComm = Number(r.offerCommissionLe ?? 0);
      const ambassadorComm = Number(r.ambassadorCommissionLe ?? 0);
      const neozoolaComm = Number((offerComm - ambassadorComm).toFixed(2));

      summary.totalOfferCommission += offerComm;
      summary.totalAmbassadorCommission += ambassadorComm;
      summary.totalNeozoolaCommission += neozoolaComm;

      // By place
      const placeKey = `${r.placeId?._id || r.placeId}`;
      const placeName = r.placeId?.name || r.placeId || 'Unknown';
      if (!summary.byPlace.has(placeKey)) {
        summary.byPlace.set(placeKey, { placeId: placeKey, placeName, total: 0, neozoola: 0, ambassador: 0, count: 0 });
      }
      const placeStats = summary.byPlace.get(placeKey);
      placeStats.total += offerComm;
      placeStats.neozoola += neozoolaComm;
      placeStats.ambassador += ambassadorComm;
      placeStats.count += 1;

      // By offer
      const offerKey = `${r.offerId}`;
      const offerName = r.offerName || r.offerId || 'Unknown';
      if (!summary.byOffer.has(offerKey)) {
        summary.byOffer.set(offerKey, { offerId: offerKey, offerName, total: 0, neozoola: 0, ambassador: 0, count: 0 });
      }
      const offerStats = summary.byOffer.get(offerKey);
      offerStats.total += offerComm;
      offerStats.neozoola += neozoolaComm;
      offerStats.ambassador += ambassadorComm;
      offerStats.count += 1;

      const category = r.placeId?.category || 'Uncategorized';
      if (!summary.byCategory.has(category)) {
        summary.byCategory.set(category, { category, total: 0, neozoola: 0, ambassador: 0, count: 0 });
      }
      const categoryStats = summary.byCategory.get(category);
      categoryStats.total += offerComm;
      categoryStats.neozoola += neozoolaComm;
      categoryStats.ambassador += ambassadorComm;
      categoryStats.count += 1;

      const ambassadorCode = r.ambassadorCode || 'No ambassador';
      if (!summary.byAmbassador.has(ambassadorCode)) {
        summary.byAmbassador.set(ambassadorCode, { code: ambassadorCode, total: 0, neozoola: 0, ambassador: 0, count: 0 });
      }
      const ambassadorStats = summary.byAmbassador.get(ambassadorCode);
      ambassadorStats.total += offerComm;
      ambassadorStats.neozoola += neozoolaComm;
      ambassadorStats.ambassador += ambassadorComm;
      ambassadorStats.count += 1;
    });

    return summary;
  }, [redemptions]);

  const sortedFinancialPlaces = useMemo(() => {
    const items = Array.from(commissionSummary.byPlace.values());
    const sectionSort = analyticsSortState.financialPlace || { key: 'total', direction: 'desc' };
    const getter = (item) => {
      switch (sectionSort.key) {
        case 'count': return item.count;
        case 'total': return item.total;
        case 'neozoola': return item.neozoola;
        case 'ambassador': return item.ambassador;
        default: return item.total;
      }
    };
    return [...items].sort((a, b) => sectionSort.direction === 'asc' ? getter(a) - getter(b) : getter(b) - getter(a));
  }, [commissionSummary.byPlace, analyticsSortState]);

  const sortedFinancialOffers = useMemo(() => {
    const items = Array.from(commissionSummary.byOffer.values());
    const sectionSort = analyticsSortState.financialOffer || { key: 'total', direction: 'desc' };
    const getter = (item) => {
      switch (sectionSort.key) {
        case 'count': return item.count;
        case 'total': return item.total;
        case 'neozoola': return item.neozoola;
        case 'ambassador': return item.ambassador;
        default: return item.total;
      }
    };
    return [...items].sort((a, b) => sectionSort.direction === 'asc' ? getter(a) - getter(b) : getter(b) - getter(a));
  }, [commissionSummary.byOffer, analyticsSortState]);

  const sortedFinancialAmbassador = useMemo(() => {
    const items = Array.from(commissionSummary.byAmbassador.values());
    const sectionSort = analyticsSortState.financialAmbassador || { key: 'total', direction: 'desc' };
    const getter = (item) => {
      switch (sectionSort.key) {
        case 'count': return item.count;
        case 'total': return item.total;
        case 'neozoola': return item.neozoola;
        case 'ambassador': return item.ambassador;
        default: return item.total;
      }
    };
    return [...items].sort((a, b) => sectionSort.direction === 'asc' ? getter(a) - getter(b) : getter(b) - getter(a));
  }, [commissionSummary.byAmbassador, analyticsSortState]);

  const sortedRedeemedByPlace = useMemo(() => {
    const items = Array.from(redeemedCommissionSummary.byPlace || []);
    const sectionSort = analyticsSortState.place || { key: 'total', direction: 'desc' };
    const getter = (item) => {
      switch (sectionSort.key) {
        case 'count': return item.count;
        case 'total': return item.total;
        case 'neozoola': return item.neozoola;
        case 'ambassador': return item.ambassador;
        default: return item.total;
      }
    };
    return [...items].sort((a, b) => sectionSort.direction === 'asc' ? getter(a) - getter(b) : getter(b) - getter(a));
  }, [redeemedCommissionSummary.byPlace, analyticsSortState]);

  const sortedRedeemedByCategory = useMemo(() => {
    const items = Array.from(redeemedCommissionSummary.byCategory || []);
    const sectionSort = analyticsSortState.category || { key: 'total', direction: 'desc' };
    const getter = (item) => {
      switch (sectionSort.key) {
        case 'count': return item.count;
        case 'total': return item.total;
        case 'neozoola': return item.neozoola;
        case 'ambassador': return item.ambassador;
        default: return item.total;
      }
    };
    return [...items].sort((a, b) => sectionSort.direction === 'asc' ? getter(a) - getter(b) : getter(b) - getter(a));
  }, [redeemedCommissionSummary.byCategory, analyticsSortState]);

  const sortedRedeemedByOffer = useMemo(() => {
    const items = Array.from(redeemedCommissionSummary.byOffer || []);
    const sectionSort = analyticsSortState.offer || { key: 'total', direction: 'desc' };
    const getter = (item) => {
      switch (sectionSort.key) {
        case 'count': return item.count;
        case 'total': return item.total;
        case 'neozoola': return item.neozoola;
        case 'ambassador': return item.ambassador;
        default: return item.total;
      }
    };
    return [...items].sort((a, b) => sectionSort.direction === 'asc' ? getter(a) - getter(b) : getter(b) - getter(a));
  }, [redeemedCommissionSummary.byOffer, analyticsSortState]);

  const sortedRedeemedByAmbassador = useMemo(() => {
    const items = Array.from(redeemedCommissionSummary.byAmbassador || []);
    const sectionSort = analyticsSortState.ambassador || { key: 'count', direction: 'desc' };
    const getter = (item) => {
      switch (sectionSort.key) {
        case 'count': return item.count;
        case 'total': return item.total;
        case 'neozoola': return item.neozoola;
        case 'ambassador': return item.ambassador;
        default: return item.count;
      }
    };
    return [...items].sort((a, b) => sectionSort.direction === 'asc' ? getter(a) - getter(b) : getter(b) - getter(a));
  }, [redeemedCommissionSummary.byAmbassador, analyticsSortState]);

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 4 }}>
        Analytics Dashboard
      </Typography>

      <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
        <Typography variant="body2">
          <strong>Note:</strong> All data shown here is based on the filters you apply in the <strong>Redemptions tab</strong>. Apply filters there to see filtered commission analytics here.
        </Typography>
      </Alert>

      <Grid container spacing={3}>
        {/* Financial Overview */}
        {commissionSummary.totalOfferCommission > 0 && (
          <Grid item xs={12}>
            <Card sx={{
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              border: '1px solid rgba(0, 0, 0, 0.05)',
              background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.08) 0%, rgba(255, 87, 34, 0.05) 100%)',
              transition: 'all 0.3s ease',
              '&:hover': { boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)' },
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <AttachMoney color="warning" />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>Financial Overview</Typography>
                </Box>

                {/* Summary chips */}
                <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
                  <Chip 
                    label={`Total Offer Commission: ${commissionSummary.totalOfferCommission.toFixed(2)} LE`} 
                    sx={{ fontWeight: 600, fontSize: '0.95rem', px: 1 }}
                  />
                  <Chip 
                    label={`Nezoola Earnings: ${commissionSummary.totalNeozoolaCommission.toFixed(2)} LE`} 
                    color="primary"
                    sx={{ fontWeight: 600, fontSize: '0.95rem', px: 1 }}
                  />
                  <Chip 
                    label={`Ambassador Paid: ${commissionSummary.totalAmbassadorCommission.toFixed(2)} LE`} 
                    color="success"
                    variant="outlined"
                    sx={{ fontWeight: 600, fontSize: '0.95rem', px: 1 }}
                  />
                </Box>

                {/* Money split breakdown */}
                {commissionSummary.totalOfferCommission > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Money Split</Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={(commissionSummary.totalNeozoolaCommission / commissionSummary.totalOfferCommission) * 100}
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>
                      <Typography variant="body2" sx={{ minWidth: 100, textAlign: 'right' }}>
                        Nezoola: {((commissionSummary.totalNeozoolaCommission / commissionSummary.totalOfferCommission) * 100).toFixed(1)}%
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      <Box sx={{ flex: 1 }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={(commissionSummary.totalAmbassadorCommission / commissionSummary.totalOfferCommission) * 100}
                          color="success"
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>
                      <Typography variant="body2" sx={{ minWidth: 100, textAlign: 'right' }}>
                        Ambassador: {((commissionSummary.totalAmbassadorCommission / commissionSummary.totalOfferCommission) * 100).toFixed(1)}%
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Top places by commission */}
                {commissionSummary.byPlace.size > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>Top Places by Commission</Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: 'rgba(255, 152, 0, 0.08)' }}>
                            <TableCell sx={{ fontWeight: 600 }}>Place</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>{renderSortButton('financialPlace', 'count', 'Redeems')}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>{renderSortButton('financialPlace', 'total', 'Total')}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>{renderSortButton('financialPlace', 'neozoola', 'Nezoola')}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>{renderSortButton('financialPlace', 'ambassador', 'Ambassador')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sortedFinancialPlaces.slice(0, 10).map((place) => (
                              <TableRow key={place.placeId}>
                                <TableCell>{place.placeName}</TableCell>
                                <TableCell align="right">{place.count}</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>{place.total.toFixed(2)} LE</TableCell>
                                <TableCell align="right" sx={{ color: 'primary.main', fontWeight: 600 }}>{place.neozoola.toFixed(2)} LE</TableCell>
                                <TableCell align="right" sx={{ color: 'success.main', fontWeight: 600 }}>{place.ambassador.toFixed(2)} LE</TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {/* Top offers by commission */}
                {commissionSummary.byOffer.size > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>Top Offers by Commission</Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: 'rgba(255, 152, 0, 0.08)' }}>
                            <TableCell sx={{ fontWeight: 600 }}>Offer</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>{renderSortButton('financialOffer', 'count', 'Redeems')}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>{renderSortButton('financialOffer', 'total', 'Total')}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>{renderSortButton('financialOffer', 'neozoola', 'Nezoola')}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>{renderSortButton('financialOffer', 'ambassador', 'Ambassador')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sortedFinancialOffers.slice(0, 10).map((offer) => (
                              <TableRow key={offer.offerId}>
                                <TableCell>{offer.offerName}</TableCell>
                                <TableCell align="right">{offer.count}</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>{offer.total.toFixed(2)} LE</TableCell>
                                <TableCell align="right" sx={{ color: 'primary.main', fontWeight: 600 }}>{offer.neozoola.toFixed(2)} LE</TableCell>
                                <TableCell align="right" sx={{ color: 'success.main', fontWeight: 600 }}>{offer.ambassador.toFixed(2)} LE</TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {commissionSummary.byAmbassador.size > 0 && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>Top Ambassadors by Commission</Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: 'rgba(255, 152, 0, 0.08)' }}>
                            <TableCell sx={{ fontWeight: 600 }}>Ambassador Code</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>{renderSortButton('financialAmbassador', 'count', 'Redeems')}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>{renderSortButton('financialAmbassador', 'total', 'Total')}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>{renderSortButton('financialAmbassador', 'neozoola', 'Nezoola Comm.')}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>{renderSortButton('financialAmbassador', 'ambassador', 'Ambassador Comm.')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sortedFinancialAmbassador.slice(0, 10).map((amb) => (
                            <TableRow key={amb.code}>
                              <TableCell>{amb.code}</TableCell>
                              <TableCell align="right">{amb.count}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>{amb.total.toFixed(2)} LE</TableCell>
                              <TableCell align="right" sx={{ color: 'primary.main', fontWeight: 600 }}>{amb.neozoola.toFixed(2)} LE</TableCell>
                              <TableCell align="right" sx={{ color: 'success.main', fontWeight: 600 }}>{amb.ambassador.toFixed(2)} LE</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

              </CardContent>
            </Card>
          </Grid>
        )}

        {typeof redeemed.totalRedeems === 'number' && (
          <Grid item xs={12}>
            <Card sx={{
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              border: '1px solid rgba(0, 0, 0, 0.05)',
              background: 'linear-gradient(135deg, rgba(46, 125, 50, 0.08) 0%, rgba(56, 249, 215, 0.05) 100%)',
              transition: 'all 0.3s ease',
              '&:hover': { boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)' },
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <LocalOffer color="success" />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>Offer redemptions</Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>{redeemed.totalRedeems}</Typography>
                <Typography variant="body2" color="text.secondary">Total redemptions across the app (read-only)</Typography>
                {(redeemed.byPlace?.length > 0 || redeemed.byOffer?.length > 0 || redeemed.byCategory?.length > 0) && (
                  <Grid container spacing={2} sx={{ mt: 2 }}>
                    {redeemedCommissionSummary.byPlace.length > 0 && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>By place</Typography>
                        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 220 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell>Place</TableCell>
                                <TableCell align="right">{renderSortButton('place', 'count', 'Redeems')}</TableCell>
                                <TableCell align="right">{renderSortButton('place', 'total', 'Total')}</TableCell>
                                <TableCell align="right">{renderSortButton('place', 'neozoola', 'Nezoola')}</TableCell>
                                <TableCell align="right">{renderSortButton('place', 'ambassador', 'Ambassador')}</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {sortedRedeemedByPlace.slice(0, 10).map((row) => (
                                <TableRow key={row.placeId}>
                                  <TableCell>{row.placeName}</TableCell>
                                  <TableCell align="right">{row.count}</TableCell>
                                  <TableCell align="right">{row.total.toFixed(2)} LE</TableCell>
                                  <TableCell align="right" sx={{ color: 'primary.main' }}>{row.neozoola.toFixed(2)} LE</TableCell>
                                  <TableCell align="right" sx={{ color: 'success.main' }}>{row.ambassador.toFixed(2)} LE</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Grid>
                    )}
                    {redeemedCommissionSummary.byCategory.length > 0 && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>By category</Typography>
                        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 220 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell>Category</TableCell>
                                <TableCell align="right">{renderSortButton('category', 'count', 'Redeems')}</TableCell>
                                <TableCell align="right">{renderSortButton('category', 'total', 'Total')}</TableCell>
                                <TableCell align="right">{renderSortButton('category', 'neozoola', 'Nezoola')}</TableCell>
                                <TableCell align="right">{renderSortButton('category', 'ambassador', 'Ambassador')}</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {sortedRedeemedByCategory.slice(0, 10).map((row, i) => (
                                <TableRow key={row.category || i}>
                                  <TableCell>{row.category}</TableCell>
                                  <TableCell align="right">{row.count}</TableCell>
                                  <TableCell align="right">{row.total.toFixed(2)} LE</TableCell>
                                  <TableCell align="right" sx={{ color: 'primary.main' }}>{row.neozoola.toFixed(2)} LE</TableCell>
                                  <TableCell align="right" sx={{ color: 'success.main' }}>{row.ambassador.toFixed(2)} LE</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Grid>
                    )}
                    {redeemedCommissionSummary.byOffer.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>By offer</Typography>
                        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 260 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell>Place</TableCell>
                                <TableCell>Offer</TableCell>
                                <TableCell align="right">{renderSortButton('offer', 'count', 'Redeems')}</TableCell>
                                <TableCell align="right">{renderSortButton('offer', 'total', 'Total')}</TableCell>
                                <TableCell align="right">{renderSortButton('offer', 'neozoola', 'Nezoola')}</TableCell>
                                <TableCell align="right">{renderSortButton('offer', 'ambassador', 'Ambassador')}</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {sortedRedeemedByOffer.slice(0, 15).map((row, i) => (
                                <TableRow key={`${row.placeId}-${row.offerId}-${i}`}>
                                  <TableCell>{row.placeName}</TableCell>
                                  <TableCell>{row.offerName || row.offerId}</TableCell>
                                  <TableCell align="right">{row.count}</TableCell>
                                  <TableCell align="right">{row.total.toFixed(2)} LE</TableCell>
                                  <TableCell align="right" sx={{ color: 'primary.main' }}>{row.neozoola.toFixed(2)} LE</TableCell>
                                  <TableCell align="right" sx={{ color: 'success.main' }}>{row.ambassador.toFixed(2)} LE</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Grid>
                    )}
                    {redeemedCommissionSummary.byAmbassador?.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>By ambassador</Typography>
                        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 260 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell>Ambassador Code</TableCell>
                                <TableCell align="right">{renderSortButton('ambassador', 'count', 'Redeems')}</TableCell>
                                <TableCell align="right">{renderSortButton('ambassador', 'total', 'Total')}</TableCell>
                                <TableCell align="right">{renderSortButton('ambassador', 'neozoola', 'Nezoola Comm.')}</TableCell>
                                <TableCell align="right">{renderSortButton('ambassador', 'ambassador', 'Ambassador Comm.')}</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {sortedRedeemedByAmbassador.slice(0, 15).map((row) => (
                                <TableRow key={row.code}>
                                  <TableCell>{row.code}</TableCell>
                                  <TableCell align="right">{row.count}</TableCell>
                                  <TableCell align="right">{row.total.toFixed(2)} LE</TableCell>
                                  <TableCell align="right" sx={{ color: 'primary.main' }}>{row.neozoola.toFixed(2)} LE</TableCell>
                                  <TableCell align="right" sx={{ color: 'success.main' }}>{row.ambassador.toFixed(2)} LE</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Grid>
                    )}
                  </Grid>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
        <Grid item xs={12}>
          <Card sx={{
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            border: '1px solid rgba(0, 0, 0, 0.05)',
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.12) 0%, rgba(118, 75, 162, 0.08) 100%)',
            transition: 'all 0.3s ease',
            '&:hover': { boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)' }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  App events (Firebase Analytics)
                </Typography>
                <Button
                  component="a"
                  href="https://console.firebase.google.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  size="small"
                >
                  Open Firebase Console
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                The app already logs these events to Firebase: place_view, search, category_select, review_submit, save_place, unsave_place. To see event counts here, link Firebase to GA4 and set GA4_PROPERTY_ID in the backend (see ANALYTICS_AND_FIREBASE.md).
              </Typography>
              {analytics.appEventsError && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {analytics.appEventsError}
                </Alert>
              )}
              {!analytics.appEventsConfigured ? (
                <Typography variant="body2" color="text.secondary">
                  GA4 not configured: set GA4_PROPERTY_ID in the backend and add the service account as Viewer in GA4. Until then, view events in Firebase Console → Analytics.
                </Typography>
              ) : (analytics.appEvents?.length > 0 ? (
                <List dense sx={{ p: 0 }}>
                  {analytics.appEvents.map((item, index) => (
                    <ListItem key={item.eventName || index} sx={{ px: 0, py: 0.5 }}>
                      <ListItemText
                        primary={item.eventName || '—'}
                        secondary={`${item.count ?? 0} events`}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No app events in the selected period. Events appear after users use the mobile app.
                </Typography>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
